import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { trackPageView } from '../../firebase/trackingService';
import SEO from '../common/SEO';

const PageContainer = styled.section`
  background-color: #121212;
  color: #FFFFFF;
  padding: 120px 24px 80px;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 100px 24px 60px;
  }
`;

const Content = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.7;
`;

const Title = styled.h1`
  font-size: 42px;
  color: #2196F3;
  margin-bottom: 16px;
  font-weight: 700;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const LastUpdated = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 48px;
  font-style: italic;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  color: #FFD700;
  margin-bottom: 16px;
  font-weight: 600;
`;


const Paragraph = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 16px;
`;

const List = styled.ul`
  margin: 16px 0;
  padding-left: 24px;
`;

const ListItem = styled.li`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 8px;
`;

const ContactInfo = styled.div`
  background-color: #1E1E1E;
  border-radius: 12px;
  padding: 24px;
  margin: 32px 0;
  border: 1px solid #333;
`;

const HighlightBox = styled.div`
  background-color: rgba(33, 150, 243, 0.1);
  border: 1px solid #2196F3;
  border-radius: 8px;
  padding: 20px;
  margin: 24px 0;
  
  p {
    margin-bottom: 0;
    color: #FFFFFF;
    font-weight: 500;
  }
`;

const TermsOfService = () => {
  useEffect(() => {
    trackPageView('Terms of Service', window.location.pathname);
  }, []);

  return (
    <>
      <SEO 
        title="Terms of Service - CarConsul"
        description="Terms of Service and User Agreement for CarConsul's AI-powered car buying advisor platform"
        pagePath="terms-of-service"
      />
      <PageContainer>
        <Content
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Title>Terms of Service</Title>
          <LastUpdated>Last Updated: December 30, 2024</LastUpdated>

          <Section>
            <SectionTitle>1. Introduction</SectionTitle>
            <Paragraph>
              These Terms of Service ("Terms", "Terms of Service") govern your use of our website located at https://carconsul.com (the "Service") operated by CarConsul ("us", "we", or "our").
            </Paragraph>
            <Paragraph>
              Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms. These Terms apply to all visitors, users and others who access or use the Service.
            </Paragraph>
            <Paragraph>
              By accessing or using our Service you agree to be bound by these Terms. If you disagree with any part of these terms then you may not access the Service.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>2. Communications</SectionTitle>
            <Paragraph>
              By creating an Account on our service, you agree to subscribe to newsletters, marketing or promotional materials and other information we may send. However, you may opt out of receiving any, or all, of these communications from us by following the unsubscribe link or instructions provided in any email we send.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>3. Purchases</SectionTitle>
            <Paragraph>
              If you wish to purchase any product or service made available through the Service ("Purchase"), you may be asked to supply certain information relevant to your Purchase including, without limitation, your credit card number, the expiration date of your credit card, your billing address, and your shipping information.
            </Paragraph>
            <Paragraph>
              You represent and warrant that: (i) you have the legal right to use any credit card(s) or other payment method(s) in connection with any Purchase; and that (ii) the information you supply to us is true, correct and complete.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>4. Contests and Promotions</SectionTitle>
            <Paragraph>
              Any contests, sweepstakes or other promotions (collectively, "Promotions") made available through the Service may be governed by rules that are separate from these Terms of Service. If you participate in any Promotions, please review the applicable rules as well as our Privacy Policy. If the rules for a Promotion conflict with these Terms of Service, the Promotion rules will apply.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>5. Accounts</SectionTitle>
            <Paragraph>
              When you create an account with us, you must provide information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account on our Service.
            </Paragraph>
            <Paragraph>
              You are responsible for safeguarding the password that you use to access the Service and for all activities that occur under your account. You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>6. Subscriptions</SectionTitle>
            <Paragraph>
              Some parts of the Service are billed on a subscription basis ("Subscription(s)"). You will be billed in advance on a recurring and periodic basis ("Billing Cycle"). Billing cycles are set either on a monthly or annual basis, depending on the type of subscription plan you select when purchasing a Subscription.
            </Paragraph>
            <Paragraph>
              At the end of each Billing Cycle, your Subscription will automatically renew under the exact same conditions unless you cancel it or CarConsul cancels it. You may cancel your Subscription renewal either through your online account management page or by contacting CarConsul customer support team.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>7. Free Trial</SectionTitle>
            <Paragraph>
              CarConsul may, at its sole discretion, offer a Subscription with a free trial for a limited period of time ("Free Trial").
            </Paragraph>
            <Paragraph>
              You may be required to enter your billing information in order to sign up for the Free Trial. If you do enter your billing information when signing up for the Free Trial, you will not be charged by CarConsul until the Free Trial has expired.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>8. Fee Changes</SectionTitle>
            <Paragraph>
              CarConsul, in its sole discretion and at any time, may modify the Subscription fees for the Subscriptions. Any Subscription fee change will become effective at the end of the then-current Billing Cycle.
            </Paragraph>
            <Paragraph>
              CarConsul will provide you with reasonable prior notice of any change in Subscription fees to give you an opportunity to terminate your Subscription before such change becomes effective.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>9. Refunds</SectionTitle>
            <Paragraph>
              Except when required by law, paid Subscription fees are non-refundable. Certain refund requests for Subscriptions may be considered by CarConsul on a case-by-case basis and granted at the sole discretion of CarConsul.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>10. Content</SectionTitle>
            <Paragraph>
              Our Service allows you to post, link, store, share and otherwise make available certain information, text, graphics, videos, or other material ("Content"). You are responsible for Content that you post to the Service, including its legality, reliability, and appropriateness.
            </Paragraph>
            <Paragraph>
              By posting Content to the Service, You represent and warrant that your use of such Content does not infringe, violate or misappropriate the rights of any third party.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>11. Links to Other Websites</SectionTitle>
            <Paragraph>
              Our Service may contain links to third-party web sites or services that are not owned or controlled by CarConsul. CarConsul has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third party web sites or services.
            </Paragraph>
            <Paragraph>
              We strongly advise you to read the terms and conditions and privacy policies of any third-party web sites or services that you visit.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>12. Intellectual Property</SectionTitle>
            <Paragraph>
              The Service and its original content, features and functionality are and will remain the exclusive property of CarConsul and its licensors. The Service is protected by copyright, trademark, and other laws. Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>13. Prohibited Uses</SectionTitle>
            <Paragraph>
              You may use Service only for lawful purposes and in accordance with the Terms. You agree not to use the Service:
            </Paragraph>
            <List>
              <ListItem>In any way that violates any applicable federal, state, local or international law or regulation</ListItem>
              <ListItem>To transmit, or procure the sending of, any advertising or promotional material, or any other form of similar solicitation</ListItem>
              <ListItem>To impersonate or attempt to impersonate the Company, a Company employee, another user, or any other person or entity</ListItem>
              <ListItem>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>14. Analytics</SectionTitle>
            <Paragraph>
              We may use third-party Service Providers to monitor and analyze the use of our Service. These analytics help us understand how the Service is used and how we can improve it.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>15. Changes to Service</SectionTitle>
            <Paragraph>
              We reserve the right to withdraw or amend our Service, and any service or material we provide on the Service, in our sole discretion without notice. We will not be liable if for any reason all or any part of the Service is unavailable at any time or for any period.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>16. Amendments to Terms</SectionTitle>
            <Paragraph>
              We may amend Terms at any time by posting the amended terms on this site. It is your responsibility to review these Terms periodically. Your continued use of the Platform following the posting of revised Terms means that you accept and agree to the changes.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>17. Limitation of Liability</SectionTitle>
            <Paragraph>
              In no event shall CarConsul, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the Service.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>18. Termination</SectionTitle>
            <Paragraph>
              We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
            </Paragraph>
            <Paragraph>
              If you wish to terminate your account, you may simply discontinue using the Service.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>19. Disclaimer of Warranty</SectionTitle>
            <Paragraph>
              These services are provided by company on an "as is" and "as available" basis. Company makes no representations or warranties of any kind, express or implied, as to the operation of their services, or the information, content or materials included therein.
            </Paragraph>
            <HighlightBox>
              <Paragraph>
                CarConsul provides AI-powered car buying advice for informational purposes only. We do not guarantee specific outcomes, savings, or results. Final purchasing decisions remain your responsibility.
              </Paragraph>
            </HighlightBox>
          </Section>

          <Section>
            <SectionTitle>20. Governing Law</SectionTitle>
            <Paragraph>
              These Terms shall be interpreted and governed by the laws of the State of Delaware, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>21. Error Reporting and Feedback</SectionTitle>
            <Paragraph>
              You may provide us with information and feedback concerning errors, suggestions for improvements, ideas, problems, complaints, and other matters related to our Service ("Feedback"). You acknowledge and agree that: (i) you shall not retain, acquire or assert any intellectual property right or other right, title or interest in or to the Feedback; (ii) Company may have development ideas similar to the Feedback; (iii) Feedback does not contain confidential information or proprietary information from you or any third party; and (iv) Company is not under any obligation of confidentiality with respect to the Feedback.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>22. Acknowledgement</SectionTitle>
            <Paragraph>
              By using Service or other services provided by us, you acknowledge that you have read these Terms of Service and agree to be bound by them.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>23. Contact Information</SectionTitle>
            <ContactInfo>
              <Paragraph style={{ marginBottom: '8px' }}>
                If you have any questions about these Terms of Service, please contact us:
              </Paragraph>
              <Paragraph style={{ marginBottom: '4px' }}>
                <strong>Email:</strong> nick@carconsul.com
              </Paragraph>
              <Paragraph style={{ marginBottom: '4px' }}>
                <strong>Website:</strong> https://carconsul.com
              </Paragraph>
              <Paragraph style={{ marginBottom: '0' }}>
                <strong>Response Time:</strong> We will respond to your inquiry within 48 hours
              </Paragraph>
            </ContactInfo>
          </Section>
        </Content>
      </PageContainer>
    </>
  );
};

export default TermsOfService;